import { ISaleOrderDetail, IPassenger, ServiceUnitEnum } from 'features/salesOrder/types';
import { formatDate } from './dateUtils';

export interface PassengerServiceItem {
	serviceKeyId: string;
	serviceType: string;
	serviceName: string;
	description: string;
	saleAmount: number;
	saleAmountInLocalCurrency?: number;
	currency: string;
	// Información adicional para Accommodation
	destination?: string;
	sharingWith?: string[]; // Nombres de otros pasajeros en la misma room
	reference?: string; // Localizador de reserva
	// Información adicional para Flight
	isReissue?: boolean;
	ticket?: string; // Número de ticket
	airline?: string; // Código de la aerolínea (vc.code)
	flightLegs?: any[]; // Itinerario de vuelo
	// Información adicional para FlightAncillary
	flightAncillaryType?: string; // Tipo de ancillary (Seat, CabinBaggage, etc.)
	// Información sobre cancelación
	isCancelled?: boolean; // Si el servicio está cancelado (enable=false)
	serviceData?: any; // Datos completos del servicio para poder mostrar más detalles
}

export interface PassengerServicesData {
	passenger: IPassenger;
	services: PassengerServiceItem[];
	totalAmount: number;
	totalAmountInLocalCurrency?: number;
}

export const groupServicesByPassenger = (salesOrder: ISaleOrderDetail): PassengerServicesData[] => {
	const passengerMap = new Map<string, PassengerServicesData>();

	// Inicializar el mapa solo con pasajeros físicos (isPhysicalPerson === true)
	salesOrder.passengers
		.filter((passenger) => passenger.isPhysicalPerson === true)
		.forEach((passenger) => {
			passengerMap.set(passenger.keyId, {
				passenger,
				services: [],
				totalAmount: 0,
				totalAmountInLocalCurrency: 0,
			});
		});

	// Procesar cada servicio
	salesOrder.services.forEach((service: any) => {
		const serviceType = service.serviceType || 'Unknown';
		const currency = service.currency || salesOrder.currency;
		let serviceName = '';
		let description = '';

		// Obtener nombre del servicio según el tipo
		switch (serviceType) {
			case 'Accommodation':
				serviceName = 'Alojamiento';
				description = service.serviceProvider?.name || service.destination?.name || '-';
				if (service.startDate && service.endDate) {
					description += ` (${formatDate(service.startDate)} - ${formatDate(service.endDate)})`;
				}
				break;
			case 'Flight':
				serviceName = 'Vuelo';
				description = service.vc?.name || service.gds || '-';
				if (service.flightLegs && service.flightLegs.length > 0) {
					const firstLeg = service.flightLegs[0];
					const lastLeg = service.flightLegs[service.flightLegs.length - 1];
					description += ` (${firstLeg.departure.airport.code} - ${lastLeg.arrival.airport.code})`;
				}
				break;
			case 'Other':
				serviceName = service.serviceName || 'Otro Servicio';
				description = service.otherServiceType || '-';
				if (service.startDate && service.endDate) {
					description += ` (${formatDate(service.startDate)} - ${formatDate(service.endDate)})`;
				}
				break;
			case 'Fee':
				serviceName = 'Fee';
				description = service.feeServiceType || '-';
				break;
			case 'FlightAncillary':
				serviceName = 'Adicional de Vuelo';
				description = service.flightAncillaryType || '-';
				break;
			case 'Transfer':
				serviceName = 'Traslado';
				description = service.transferSegments?.[0]?.originName || '-';
				if (service.transferSegments?.[0]?.destinationName) {
					description += ` - ${service.transferSegments[0].destinationName}`;
				}
				break;
			default:
				serviceName = serviceType;
				description = service.serviceName || service.reference || '-';
		}

		// Verificar si el servicio está cancelado (enable=false)
		const isServiceCancelled = service.enable === false;

		// Filtrar servicios Fee con saleAmount = 0
		if (serviceType === 'Fee') {
			const totalSaleAmount = parseFloat(service.totalSaleAmount || service.saleAmount || '0');
			if (totalSaleAmount === 0) {
				return; // No incluir este servicio
			}
		}

		// Si el servicio está cancelado y tiene valor 0, excluirlo completamente
		if (isServiceCancelled) {
			const totalSaleAmount = parseFloat(service.totalSaleAmount || service.saleAmount || '0');
			if (totalSaleAmount === 0) {
				return; // No incluir este servicio
			}
		}

		// Procesar según el tipo de servicio
		if (serviceType === 'Accommodation' && service.rooms) {
			// Servicios de alojamiento
			if (service.unit === ServiceUnitEnum.Group) {
				// Por Grupo: Dividir totalSaleAmount entre cantidad de habitaciones, luego entre pasajeros físicos de cada habitación
				const totalRooms = service.rooms.length;
				const totalSaleAmount = parseFloat(service.totalSaleAmount || '0');
				const amountPerRoom = totalRooms > 0 ? totalSaleAmount / totalRooms : 0;

				// Calcular monto en moneda local a nivel de servicio
				const totalSaleAmountInLocalCurrency = parseFloat((service as any).totalSaleAmountInLocalCurrency || '0');
				const amountPerRoomInLocalCurrency = totalRooms > 0 ? totalSaleAmountInLocalCurrency / totalRooms : 0;

				service.rooms.forEach((room: any) => {
					if (room.passengerIds && room.passengerIds.length > 0) {
						// Filtrar solo pasajeros físicos en esta habitación
						const physicalPassengersInRoom = room.passengerIds.filter((pid: string) => passengerMap.has(pid));
						const physicalPassengersCount = physicalPassengersInRoom.length;

						if (physicalPassengersCount > 0) {
							const amountPerPassenger = amountPerRoom / physicalPassengersCount;
							const amountPerPassengerInLocalCurrency =
								physicalPassengersCount > 0 ? amountPerRoomInLocalCurrency / physicalPassengersCount : 0;

							// Obtener nombres de todos los pasajeros en esta room
							const roomPassengerNames = room.passengerIds
								.map((pid: string) => {
									const p = salesOrder.passengers.find((pass: any) => pass.keyId === pid);
									return p ? `${p.name} ${p.lastName}`.toUpperCase() : null;
								})
								.filter((name: string | null) => name !== null);

							physicalPassengersInRoom.forEach((passengerId: string) => {
								const passengerData = passengerMap.get(passengerId);
								if (passengerData) {
									// Obtener nombres de otros pasajeros (excluyendo el actual)
									const currentPassenger = salesOrder.passengers.find((p: any) => p.keyId === passengerId);
									const currentPassengerName = currentPassenger
										? `${currentPassenger.name} ${currentPassenger.lastName}`.toUpperCase()
										: '';
									const sharingWith = roomPassengerNames.filter((name: string) => name !== currentPassengerName);

									const roomDescription = `${description} - ${room.room || 'Habitación'} (${room.board || '-'})`;

									passengerData.services.push({
										serviceKeyId: service.keyId,
										serviceType,
										serviceName,
										description: roomDescription,
										saleAmount: amountPerPassenger,
										saleAmountInLocalCurrency: amountPerPassengerInLocalCurrency || undefined,
										currency,
										destination: service.destination?.name,
										sharingWith: sharingWith.length > 0 ? sharingWith : undefined,
										reference: service.reference,
										isCancelled: isServiceCancelled || undefined,
										serviceData: service,
									});
									passengerData.totalAmount += amountPerPassenger;
									if (amountPerPassengerInLocalCurrency > 0) {
										passengerData.totalAmountInLocalCurrency =
											(passengerData.totalAmountInLocalCurrency || 0) + amountPerPassengerInLocalCurrency;
									}
								}
							});
						}
					}
				});
			} else {
				// Por Habitación (PerRoom): Dividir room.saleAmount entre pasajeros físicos de la habitación
				service.rooms.forEach((room: any) => {
					if (room.passengerIds && room.passengerIds.length > 0 && room.saleAmount) {
						// Filtrar solo pasajeros físicos en esta habitación
						const physicalPassengersInRoom = room.passengerIds.filter((pid: string) => passengerMap.has(pid));
						const physicalPassengersCount = physicalPassengersInRoom.length;

						if (physicalPassengersCount > 0) {
							const roomSaleAmount = parseFloat(room.saleAmount) || 0;
							const amountPerPassenger = roomSaleAmount / physicalPassengersCount;

							// Obtener nombres de todos los pasajeros en esta room
							const roomPassengerNames = room.passengerIds
								.map((pid: string) => {
									const p = salesOrder.passengers.find((pass: any) => pass.keyId === pid);
									return p ? `${p.name} ${p.lastName}`.toUpperCase() : null;
								})
								.filter((name: string | null) => name !== null);

							physicalPassengersInRoom.forEach((passengerId: string) => {
								const passengerData = passengerMap.get(passengerId);
								if (passengerData) {
									// Obtener nombres de otros pasajeros (excluyendo el actual)
									const currentPassenger = salesOrder.passengers.find((p: any) => p.keyId === passengerId);
									const currentPassengerName = currentPassenger
										? `${currentPassenger.name} ${currentPassenger.lastName}`.toUpperCase()
										: '';
									const sharingWith = roomPassengerNames.filter((name: string) => name !== currentPassengerName);

									const roomDescription = `${description} - ${room.room || 'Habitación'} (${room.board || '-'})`;
									// Calcular monto en moneda local si está disponible (para PerRoom, no hay totalSaleAmountInLocalCurrency a nivel de room)
									const roomSaleAmountInLocalCurrency = parseFloat((room as any).saleAmountInLocalCurrency || '0');
									const amountPerPassengerInLocalCurrency =
										physicalPassengersCount > 0 ? roomSaleAmountInLocalCurrency / physicalPassengersCount : 0;

									passengerData.services.push({
										serviceKeyId: service.keyId,
										serviceType,
										serviceName,
										description: roomDescription,
										saleAmount: amountPerPassenger,
										saleAmountInLocalCurrency: amountPerPassengerInLocalCurrency || undefined,
										currency,
										destination: service.destination?.name,
										sharingWith: sharingWith.length > 0 ? sharingWith : undefined,
										reference: service.reference,
										isCancelled: isServiceCancelled || undefined,
										serviceData: service,
									});
									passengerData.totalAmount += amountPerPassenger;
									if (amountPerPassengerInLocalCurrency > 0) {
										passengerData.totalAmountInLocalCurrency =
											(passengerData.totalAmountInLocalCurrency || 0) + amountPerPassengerInLocalCurrency;
									}
								}
							});
						}
					}
				});
			}
		} else if (serviceType === 'Flight' && service.passengersGroupByPassengerType) {
			// Servicios de vuelo: cada grupo tiene passengerIds y saleAmount (ya es el valor por pasajero)
			service.passengersGroupByPassengerType.forEach((group: any) => {
				if (group.passengerIds && group.passengerIds.length > 0 && group.saleAmount) {
					// group.saleAmount ya es el valor por pasajero del grupo tarifario
					const amountPerPassenger = parseFloat(group.saleAmount);

					// Filtrar solo pasajeros físicos en este grupo
					const physicalPassengersInGroup = group.passengerIds.filter((pid: string) => passengerMap.has(pid));

					physicalPassengersInGroup.forEach((passengerId: string) => {
						const passengerData = passengerMap.get(passengerId);
						if (passengerData) {
							// Buscar el ticket del pasajero en passengersTickets
							let ticketNumber = '-';
							if (group.passengersTickets && Array.isArray(group.passengersTickets)) {
								const passengerTicket = group.passengersTickets.find((pt: any) => pt.passengerId === passengerId);
								if (passengerTicket && passengerTicket.ticket) {
									ticketNumber = passengerTicket.ticket;
								}
							}

							// Calcular monto en moneda local si está disponible
							// group.saleAmountInLocalCurrency ya es el valor por pasajero (igual que group.saleAmount)
							const amountPerPassengerInLocalCurrency = parseFloat((group as any).saleAmountInLocalCurrency || '0');

							passengerData.services.push({
								serviceKeyId: service.keyId,
								serviceType,
								serviceName,
								description: `${description} (Tipo: ${group.passengerType || 'A'})`,
								saleAmount: amountPerPassenger,
								saleAmountInLocalCurrency: amountPerPassengerInLocalCurrency || undefined,
								currency,
								isReissue: service.isReissue || false,
								reference: service.reference,
								ticket: ticketNumber,
								airline: service.vc?.code,
								flightLegs: service.flightLegs && service.flightLegs.length > 0 ? service.flightLegs : undefined,
								isCancelled: isServiceCancelled || undefined,
								serviceData: service,
							});
							passengerData.totalAmount += amountPerPassenger;
							if (amountPerPassengerInLocalCurrency > 0) {
								passengerData.totalAmountInLocalCurrency =
									(passengerData.totalAmountInLocalCurrency || 0) + amountPerPassengerInLocalCurrency;
							}
						}
					});
				}
			});
		} else if (service.passengerIds && service.passengerIds.length > 0) {
			// Otros servicios: tienen passengerIds directo
			const totalSaleAmount = parseFloat(service.totalSaleAmount || service.saleAmount || '0');
			// Filtrar Fee con valor 0
			if (serviceType === 'Fee' && totalSaleAmount === 0) {
				return; // No incluir este servicio
			}

			// Filtrar solo pasajeros físicos
			const physicalPassengers = service.passengerIds.filter((pid: string) => passengerMap.has(pid));
			const physicalPassengersCount = physicalPassengers.length;

			if (physicalPassengersCount > 0) {
				const amountPerPassenger = totalSaleAmount / physicalPassengersCount;
				// Calcular monto en moneda local si está disponible
				const totalSaleAmountInLocalCurrency = parseFloat((service as any).totalSaleAmountInLocalCurrency || '0');
				const amountPerPassengerInLocalCurrency = totalSaleAmountInLocalCurrency / physicalPassengersCount;

				physicalPassengers.forEach((passengerId: string) => {
					const passengerData = passengerMap.get(passengerId);
					if (passengerData) {
						passengerData.services.push({
							serviceKeyId: service.keyId,
							serviceType,
							serviceName,
							description,
							saleAmount: amountPerPassenger,
							saleAmountInLocalCurrency: amountPerPassengerInLocalCurrency || undefined,
							currency,
							flightAncillaryType: serviceType === 'FlightAncillary' ? service.flightAncillaryType : undefined,
							isCancelled: isServiceCancelled || undefined,
							serviceData: service,
						});
						passengerData.totalAmount += amountPerPassenger;
						if (amountPerPassengerInLocalCurrency > 0) {
							passengerData.totalAmountInLocalCurrency =
								(passengerData.totalAmountInLocalCurrency || 0) + amountPerPassengerInLocalCurrency;
						}
					}
				});
			}
		}
	});

	// Convertir el mapa a array y ordenar (principal primero, luego alfabético)
	const result = Array.from(passengerMap.values()).sort((a, b) => {
		if (a.passenger.mainPassenger && !b.passenger.mainPassenger) return -1;
		if (!a.passenger.mainPassenger && b.passenger.mainPassenger) return 1;
		const nameA = `${a.passenger.name} ${a.passenger.lastName}`.toLowerCase();
		const nameB = `${b.passenger.name} ${b.passenger.lastName}`.toLowerCase();
		return nameA.localeCompare(nameB);
	});

	// Ordenar servicios por fecha de inicio si está disponible
	result.forEach((passengerData) => {
		passengerData.services.sort((a, b) => {
			// Por ahora ordenamos por tipo de servicio y luego por monto
			if (a.serviceType !== b.serviceType) {
				return a.serviceType.localeCompare(b.serviceType);
			}
			return b.saleAmount - a.saleAmount;
		});
	});

	return result;
};
